/*
 * rs-preise.js: Rechenkern für Buybox, Warenkorb, Größen-Finder und Preistreppe (05 Abschnitt 8.1).
 *
 * Reine Rechnung, ohne DOM, ohne Netz. Alle Beträge sind ganze Cent. Kein Betrag steht fest im Code:
 * Preise kommen aus den Varianten, Nachlass und Versandschwelle aus den Theme-Settings
 * (rs_rabatt_zweier in Prozent, rs_versand_schwelle in Euro und in Cent übergeben).
 *
 * Laden (vor rs-buybox.js und rs-cart.js):
 *   <script type="module" src="{{ 'rs-preise.js' | asset_url }}"></script>
 * Nutzen: als ES-Modul (import) oder über window.RSPreise (gleiche Funktionen).
 *
 * Duo 20 % (01.10.2026): „Zwei Pfannen“ ist eine Variante des Bundles pfannen-duo (Farbe und Größenpaar). Ihr Preis
 * ist floor((a + b) * (100 - p) / 100) in Cent (summeZweier), p = rs_rabatt_zweier in Prozent, auf den Cent
 * abgerundet, damit die Ersparnis nie unter p Prozent liegt. Ein Paar ist nur kaufbar, wenn seine Duo-Variante da
 * ist, lieferbar ist und centgenau diesen Preis hat (zweierPaar); sonst sperrt die Buybox beide Knöpfe. Gleiche
 * Formel in snippets/rs-duo-preis.liquid, im Rückfall von rs-buybox.js (duoSumme) und in tools/relaunch/duo-bundle.mjs.
 * Die Regel „Pfannen-Duo“ (Kaufe 1, erhalte 1, E-185) geht beim Live-Gang aus. Der Warenkorb rechnet deshalb ohne
 * eigene Paarbildung (summeWarenkorb): Einzelpfannen, Pfannen-Duo, Set und Bambus zu ihren Preisen. Liegen Einzelpfannen
 * im Warenkorb, bieten die Karten den Tausch gegen ein Pfannen-Duo an (warenkorbKarten, Wache zweierPaar).
 *
 * Der Warenkorb zeigt immer die echten Werte aus /cart.js. Eigene Rechnungen gibt es nur für
 * Vorschläge (Karten, Versandzeile) und für die Buybox vor dem Hinzufügen (02, Grundregeln).
 *
 * Datenform "daten" (Preise je Farbe und Größe, Farbe als Schlüssel "1" oder "2" wie rs_farbe_1/2):
 *   {
 *     prozent: 20,                                   // settings.rs_rabatt_zweier, Prozent auf beide Pfannen des Duos
 *     schwelle: 6900,                                // settings.rs_versand_schwelle mal 100
 *     einzel: { "1": { "20": Eintrag, "24": Eintrag, "28": Eintrag }, "2": { ... } },
 *     set:    { "1": Eintrag, "2": Eintrag },
 *     bambus: Eintrag,
 *     duoVarianten: { "20-28|1": Eintrag, ... }      // Pfannen-Duo je Paar und Farbe (duoSchluessel)
 *   }
 *   Eintrag = Cent als Zahl, oder { id, preis, verfuegbar } (auch { id, price, available }).
 *   Fehlt "verfuegbar", gilt der Eintrag als lieferbar. Fehlt der Preis, rechnen die Funktionen
 *   nicht und geben null zurück (Buybox zeigt dann den Platzhalter).
 */

export const GROESSEN = Object.freeze([20, 24, 28]);

// Wortlaut 04 A5.3; rs-cart.js darf eigene Texte aus den Settings übergeben.
export const VERSAND_TEXTE = Object.freeze({
  frei: 'Dein Versand ist kostenlos.',
  noch_ein_produkt: 'Noch ein Produkt, dann ist dein Versand kostenlos.',
  rueckfall: 'Leg noch etwas dazu, dann ist dein Versand kostenlos.'
});

const NBSP = ' ';

/* ---------- kleine Helfer ---------- */

function zahl(x) {
  if (typeof x === 'number') return Number.isFinite(x) ? x : null;
  if (typeof x === 'string' && /^\s*-?\d+(\.\d+)?\s*$/.test(x)) return Number(x);
  return null;
}

// Preis eines Eintrags in Cent oder null
export function preisVon(eintrag) {
  if (eintrag == null) return null;
  if (typeof eintrag === 'object') {
    const p = eintrag.preis !== undefined ? eintrag.preis : eintrag.price;
    return zahl(p);
  }
  return zahl(eintrag);
}

// Lieferbarkeit eines Eintrags; ohne Angabe lieferbar, ohne Preis nie
export function verfuegbarVon(eintrag) {
  if (preisVon(eintrag) === null) return false;
  if (typeof eintrag === 'object') {
    const v = eintrag.verfuegbar !== undefined ? eintrag.verfuegbar : eintrag.available;
    return v === undefined ? true : Boolean(v);
  }
  return true;
}

function idVon(eintrag) {
  if (eintrag && typeof eintrag === 'object' && eintrag.id != null) return eintrag.id;
  return null;
}

// kaufmännisch auf ganze Cent: ab ,5 aufwärts (für positive Werte)
function halbAuf(wert) {
  return Math.floor(wert + 0.5 + 1e-9);
}

function summe(liste, fn) {
  return liste.reduce((t, x) => t + fn(x), 0);
}

function einzelDerFarbe(daten, farbe) {
  const e = daten && daten.einzel;
  if (!e || farbe == null) return {};
  return e[String(farbe)] || {};
}

function eintragGroesse(einzelFarbe, g) {
  if (!einzelFarbe) return undefined;
  return einzelFarbe[g] !== undefined ? einzelFarbe[g] : einzelFarbe[String(g)];
}

function setDerFarbe(daten, farbe) {
  const s = daten && daten.set;
  if (!s || farbe == null) return undefined;
  return s[String(farbe)];
}

/* ---------- Anzeige ---------- */

// „54,99 €“ mit geschütztem Leerzeichen; kurz: ganze Euro ohne ",00" („69 €“)
export function formatEuro(cent, { kurz = false } = {}) {
  const t = betragText(cent, kurz);
  return t ? t + NBSP + '€' : '';
}

// für aria-label: „87,98 Euro“ (04 A5.5)
export function formatEuroSprache(cent, { kurz = false } = {}) {
  const t = betragText(cent, kurz);
  return t ? t + ' Euro' : '';
}

function betragText(cent, kurz) {
  const c = zahl(cent);
  if (c === null) return '';
  const ganz = Math.round(c);
  const abs = Math.abs(ganz);
  const euro = Math.floor(abs / 100);
  const rest = abs % 100;
  const euroText = String(euro).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const text = kurz && rest === 0 ? euroText : euroText + ',' + String(rest).padStart(2, '0');
  return (ganz < 0 ? '-' : '') + text;
}

// Setting in Euro (etwa rs_versand_schwelle = 69) in Cent
export function euroZuCent(euro) {
  const e = typeof euro === 'string' ? zahl(euro.replace(',', '.')) : zahl(euro);
  return e === null ? null : Math.round(e * 100);
}

/* ---------- Pfannen-Duo und Summen ---------- */

/*
 * Duo 20 % (01.10.2026): Preis eines Pfannen-Duos aus zwei Einzelpreisen, floor((a + b) * (100 - p) / 100) in Cent.
 * Einträge oder Cent; p ganzzahlig von 0 bis 99, sonst null (ebenso ohne Preis). Abgerundet, damit die Ersparnis nie
 * unter p Prozent liegt; die Rundung je Stück aus E-184 könnte „20 %“ aufrunden und kommt nicht zurück.
 * Gleiche Formel in snippets/rs-duo-preis.liquid, rs-buybox.js duoSumme und tools/relaunch/duo-bundle.mjs.
 */
export function summeZweier(centA, centB, prozent) {
  const a = preisVon(centA);
  const b = preisVon(centB);
  const p = zahl(prozent);
  if (a === null || b === null || p === null || !Number.isInteger(p) || p < 0 || p >= 100) return null;
  return Math.floor(((a + b) * (100 - p)) / 100);
}

// Ersparnis des Pfannen-Duos gegen die zwei vollen Einzelpreise („Du sparst 28,00 €“); null wie summeZweier
export function ersparnisZweier(centA, centB, prozent) {
  const s = summeZweier(centA, centB, prozent);
  return s === null ? null : preisVon(centA) + preisVon(centB) - s;
}

// Schlüssel einer Duo-Variante in daten.duoVarianten: „20-28|1“, kleine Größe zuerst, Farbe 1 oder 2; null ohne Angaben
export function duoSchluessel(groesseA, groesseB, farbe) {
  const a = zahl(groesseA);
  const b = zahl(groesseB);
  if (a === null || b === null || farbe == null || farbe === '') return null;
  return Math.min(a, b) + '-' + Math.max(a, b) + '|' + String(farbe);
}

/*
 * Duo 20 % (01.10.2026): ein Paar der Farbe als Pfannen-Duo, die eine Wache für „In den Warenkorb“, Express, Karten
 * und „ab“-Preise. Kaufbar nur, wenn die Duo-Variante existiert, lieferbar ist und ihr Preis centgenau summeZweier
 * gleicht. Sonst nennt grund den ersten Fehler: 'duo_fehlt' (Variante, ID oder Preis fehlt), 'duo_preis' (Preis weicht
 * ab, Pflege: tools/relaunch/duo-bundle.mjs --preise) oder 'duo_aus' (nicht lieferbar). einzelnLieferbar: beide
 * Einzelpfannen bestellbar (Vergleich „einzeln …“ nur dann, 07 3.3). Reihenfolge der Größen egal.
 * Rückgabe { a, b, einzeln, summe, ersparnis, id, kaufbar, grund, einzelnLieferbar }; null, wenn ein Einzelpreis oder
 * ein gültiger Prozentwert fehlt.
 */
export function zweierPaar(daten, farbe, groesse1, groesse2) {
  if (!daten) return null;
  const g1 = zahl(groesse1);
  const g2 = zahl(groesse2);
  if (g1 === null || g2 === null || farbe == null) return null;
  const a = Math.min(g1, g2);
  const b = Math.max(g1, g2);
  const einzelFarbe = einzelDerFarbe(daten, farbe);
  const ea = eintragGroesse(einzelFarbe, a);
  const eb = eintragGroesse(einzelFarbe, b);
  const summeCent = summeZweier(ea, eb, daten.prozent);
  if (summeCent === null) return null;
  const einzeln = preisVon(ea) + preisVon(eb);
  const duo = (daten.duoVarianten || {})[duoSchluessel(a, b, farbe)];
  const id = idVon(duo);
  let grund = null;
  if (duo == null || id == null || preisVon(duo) === null) grund = 'duo_fehlt';
  else if (preisVon(duo) !== summeCent) grund = 'duo_preis';
  else if (!verfuegbarVon(duo)) grund = 'duo_aus';
  return {
    a,
    b,
    einzeln,
    summe: summeCent,
    ersparnis: einzeln - summeCent,
    id,
    kaufbar: grund === null,
    grund,
    einzelnLieferbar: verfuegbarVon(ea) && verfuegbarVon(eb)
  };
}

// Liste von Positionen: Zahl = ein Stück zu diesem Preis, sonst { cent|preis, menge, farbe }
function positionenAus(teil) {
  if (teil == null) return [];
  const liste = Array.isArray(teil) ? teil : [teil];
  return liste.map((x) => {
    if (typeof x === 'number' || typeof x === 'string') return { cent: zahl(x), menge: 1, farbe: null };
    const cent = zahl(x.cent !== undefined ? x.cent : x.preis !== undefined ? x.preis : x.price);
    const menge = x.menge !== undefined ? zahl(x.menge) : 1;
    return { cent, menge, farbe: x.farbe != null ? String(x.farbe) : null, groesse: x.groesse != null ? x.groesse : null };
  });
}

/*
 * Summe eines Warenkorbs zu den Preisen der Positionen. Duo 20 % (01.10.2026): keine eigene Paarbildung mehr, die Regel
 * „Pfannen-Duo“ (E-185) geht aus. Den Nachlass für zwei Pfannen gibt es nur als Pfannen-Duo, er steckt in dessen Preis
 * (summeZweier); was Shopify sonst abzieht (Rabattcodes), steht in den Zeilen aus /cart.js.
 *   korb.einzel: Einzelpfannen, je Eintrag eine Position (Zahl = ein Stück, oder { cent, menge, farbe, groesse })
 *   korb.duo, korb.set, korb.bambus: Zahl = Summe in Cent dieser Positionen, oder eine Liste wie bei einzel
 * Alles geht voll ein, nachlass ist immer 0. zeilen: die Einzelpfannen { cent, menge, farbe, groesse, brutto, nachlass,
 * summe }. Rückgabe: { brutto, nachlass, gesamt, stueckEinzel, zeilen } oder null, wenn ein Preis oder eine Menge fehlt.
 */
export function summeWarenkorb(korb) {
  const einzel = positionenAus(korb && korb.einzel);
  const nebenSumme = (teil) => {
    if (teil == null) return 0;
    if (typeof teil === 'number' || typeof teil === 'string') return zahl(teil);
    const liste = positionenAus(teil);
    if (liste.some((z) => z.cent === null || z.menge === null)) return null;
    return summe(liste, (z) => z.cent * z.menge);
  };
  const duoSumme = nebenSumme(korb && korb.duo);
  const setSumme = nebenSumme(korb && korb.set);
  const bambusSumme = nebenSumme(korb && korb.bambus);
  if (duoSumme === null || setSumme === null || bambusSumme === null) return null;
  if (einzel.some((z) => z.cent === null || z.menge === null)) return null;
  const zeilen = einzel.map((z) => {
    const brutto = z.cent * z.menge;
    return { cent: z.cent, menge: z.menge, farbe: z.farbe, groesse: z.groesse, brutto, nachlass: 0, summe: brutto };
  });
  const brutto = summe(zeilen, (z) => z.brutto) + duoSumme + setSumme + bambusSumme;
  return { brutto, nachlass: 0, gesamt: brutto, stueckEinzel: summe(einzel, (z) => z.menge), zeilen };
}

/* ---------- Vergleich „Einzeln zusammen …, du sparst …“ (01 E-62) ---------- */

// Nur mit echtem Vergleichswert und nur, wenn wirklich gespart wird; sonst null.
export function vergleich(summeCent, einzelnSummeCent) {
  const s = zahl(summeCent);
  const e = zahl(einzelnSummeCent);
  if (s === null || e === null || e <= s) return null;
  return { summe: s, einzelnSumme: e, ersparnis: e - s };
}

/*
 * Set gegen die drei Einzelpreise der Farbe (Set-Karte, Preisblock, Tipp-Karte).
 * einzel: [20er, 24er, 28er] oder { 20: …, 24: …, 28: … } als Cent oder Eintrag.
 * Ist eine Größe nicht bestellbar oder fehlt ein Preis, gibt es keinen Vergleich (07 3.3): null.
 * prozent ist abgerundet (Auftrag 4.3, 07 3.4), jePfanne kaufmännisch gerundet.
 */
export function setErsparnis(einzel, setCent) {
  const liste = Array.isArray(einzel) ? einzel : einzel && typeof einzel === 'object' ? GROESSEN.map((g) => eintragGroesse(einzel, g)) : null;
  const set = preisVon(setCent);
  if (!liste || liste.length !== 3 || set === null) return null;
  if (!liste.every((e) => verfuegbarVon(e))) return null;
  const einzelnSumme = summe(liste, (e) => preisVon(e));
  const ersparnis = einzelnSumme - set;
  if (ersparnis <= 0) return null;
  return {
    set,
    einzelnSumme,
    ersparnis,
    prozent: Math.floor((ersparnis * 100) / einzelnSumme + 1e-9),
    jePfanne: halbAuf(set / 3)
  };
}

// Versand frei ab der Schwelle, Basis ist die Summe nach Rabatten (02 Abschnitt 6). Alles in Cent.
export function versandFrei(summeCent, schwelleCent) {
  const s = zahl(summeCent);
  const w = zahl(schwelleCent);
  if (s === null || w === null) return false;
  return s >= w;
}

// Füllstand des Versandbalkens von 0 bis 1
export function versandAnteil(summeCent, schwelleCent) {
  const s = zahl(summeCent);
  const w = zahl(schwelleCent);
  if (s === null || w === null) return 0;
  if (w <= 0) return 1;
  return Math.min(1, Math.max(0, s / w));
}

/* ---------- Buybox, Preistreppe, Größen-Finder ---------- */

/*
 * Preis der Auswahl in der Buybox (05 8.2, Zustand { modus, farbe, groesse1, groesse2, zusatz }).
 * Rückgabe: { summe, einzelnSumme, ersparnis, nachlass, verfuegbar, versandFrei, ids, stueck, sperre }
 *   einzelnSumme und ersparnis nur beim Zweier und beim Set, und nur wenn alle verglichenen
 *   Einzelpfannen bestellbar sind; sonst null (keine Vergleichszeile, 07 3.3).
 *   Mit Zusatz steht der Bambus-Küchenhelfer mit vollem Preis in Summe und Vergleichswert,
 *   die Ersparnis bleibt gleich (01 E-62).
 *   E-169: einzelnSumme ist beim Set die Summe der drei vollen Einzelpreise (setErsparnis); das Ereignis
 *   rs:buybox:geaendert und der Preisblock der Buybox (vergleichDerWahl) nennen denselben Wert.
 *   Duo 20 % (01.10.2026): Der Zweier ist genau eine Position, die Duo-Variante mit Menge 1 (ids), zum Preis aus
 *   summeZweier, auch wenn das Paar gesperrt ist. verfuegbar folgt der Wache zweierPaar, sperre nennt ihren Grund
 *   ('duo_fehlt', 'duo_preis', 'duo_aus'; sonst und bei Set und Eine null). daten.prozent braucht nur der Zweier.
 * null, wenn ein benötigter Preis fehlt.
 */
export function preisDerWahl(wahl, daten) {
  if (!wahl || !daten) return null;
  const farbe = wahl.farbe != null ? String(wahl.farbe) : null;
  const einzelFarbe = einzelDerFarbe(daten, farbe);
  let summeCent = null;
  let einzelnSumme = null;
  let nachlass = 0;
  let verfuegbar = true;
  let stueck = 0;
  let sperre = null;
  const ids = [];

  if (wahl.modus === 'set') {
    const s = setDerFarbe(daten, farbe);
    summeCent = preisVon(s);
    verfuegbar = verfuegbarVon(s);
    stueck = 3;
    ids.push({ id: idVon(s), menge: 1 });
    const e = setErsparnis(GROESSEN.map((g) => eintragGroesse(einzelFarbe, g)), s);
    if (e) einzelnSumme = e.einzelnSumme;
  } else if (wahl.modus === 'eine') {
    const e = eintragGroesse(einzelFarbe, wahl.groesse1);
    summeCent = preisVon(e);
    verfuegbar = verfuegbarVon(e);
    stueck = 1;
    ids.push({ id: idVon(e), menge: 1 });
  } else if (wahl.modus === 'zwei') {
    const pz = zweierPaar(daten, farbe, wahl.groesse1, wahl.groesse2);
    if (!pz) return null;
    summeCent = pz.summe;
    nachlass = pz.ersparnis;
    verfuegbar = pz.kaufbar;
    sperre = pz.grund;
    stueck = 2;
    ids.push({ id: pz.id, menge: 1 });
    if (pz.kaufbar && pz.einzelnLieferbar) einzelnSumme = pz.einzeln;
  } else {
    return null;
  }
  if (summeCent === null) return null;

  if (wahl.zusatz) {
    const bambus = daten.bambus;
    const pb = preisVon(bambus);
    if (pb === null) return null;
    summeCent += pb;
    if (einzelnSumme !== null) einzelnSumme += pb;
    verfuegbar = verfuegbar && verfuegbarVon(bambus);
    ids.push({ id: idVon(bambus), menge: 1 });
  }

  const v = einzelnSumme !== null ? vergleich(summeCent, einzelnSumme) : null;
  return {
    summe: summeCent,
    einzelnSumme: v ? v.einzelnSumme : null,
    ersparnis: v ? v.ersparnis : null,
    nachlass,
    verfuegbar,
    versandFrei: versandFrei(summeCent, daten.schwelle),
    ids,
    stueck,
    sperre
  };
}

/*
 * E-168/E-169: Vergleich für den Preisblock der Buybox, nie als Strichpreis. Seit E-169 (Denny, 29.09.2026) steht
 * „einzeln {einzeln}“ klein neben dem Preis und die Plakette dahinter (E-170). Zustand wie bei preisDerWahl. Rückgabe:
 *   Zweier { art: 'zwei', einzeln, ersparnis, prozent }: einzeln ist die Summe der beiden vollen Einzelpreise,
 *     ersparnis die des Pfannen-Duos (zweierPaar, Duo 20 % (01.10.2026)); nur bei einem Nachlass über 0, nur mit
 *     kaufbarem Pfannen-Duo und nur, wenn beide Einzelpfannen bestellbar sind.
 *   Set { art: 'set', einzeln, ersparnis }: einzeln ist die Summe der drei vollen Einzelpreise (setErsparnis). Der
 *     Set-Vergleich hängt nicht am Nachlass für zwei Pfannen und gilt auch bei 0 (E-169, E-184).
 *   Mit Zusatz kommt der Preis des Bambus-Küchenhelfers zu einzeln dazu, die Ersparnis bleibt gleich;
 *   fehlt sein Preis, null.
 * null bei „Eine Pfanne“, bei fehlendem Preis und ohne echte Ersparnis.
 */
export function vergleichDerWahl(wahl, daten) {
  if (!wahl || !daten) return null;
  const farbe = wahl.farbe != null ? String(wahl.farbe) : null;
  const einzelFarbe = einzelDerFarbe(daten, farbe);
  let v = null;
  if (wahl.modus === 'zwei') {
    const prozent = zahl(daten.prozent);
    if (prozent === null || prozent <= 0) return null;
    const pz = zweierPaar(daten, farbe, wahl.groesse1, wahl.groesse2);
    if (!pz || !pz.kaufbar || !pz.einzelnLieferbar || pz.ersparnis <= 0) return null;
    v = { art: 'zwei', einzeln: pz.einzeln, ersparnis: pz.ersparnis, prozent };
  } else if (wahl.modus === 'set') {
    const e = setErsparnis(GROESSEN.map((g) => eintragGroesse(einzelFarbe, g)), setDerFarbe(daten, farbe));
    if (!e) return null;
    v = { art: 'set', einzeln: e.einzelnSumme, ersparnis: e.ersparnis };
  } else {
    return null;
  }
  if (wahl.zusatz) {
    const pb = preisVon(daten.bambus);
    if (pb === null) return null;
    v.einzeln += pb;
  }
  return v;
}

/*
 * Alle sechs Zweier einer Farbe (Preistreppe, Aufklapper, Karten; 02 Abschnitt 2). Duo 20 % (01.10.2026): je Zeile das
 * Ergebnis von zweierPaar (mit Wache) und jePfanne, der Duo-Preis halbiert und kaufmännisch gerundet (nur Anzeige).
 * Je Zeile: { a, b, einzeln, summe, ersparnis, id, kaufbar, grund, einzelnLieferbar, jePfanne }
 * Zeilen mit fehlendem Preis entfallen.
 */
export function zweierTabelle(daten, farbe) {
  const zeilen = [];
  GROESSEN.forEach((a, i) => {
    GROESSEN.slice(i).forEach((b) => {
      const pz = zweierPaar(daten, farbe, a, b);
      if (!pz) return;
      zeilen.push(Object.assign(pz, { jePfanne: halbAuf(pz.summe / 2) }));
    });
  });
  return zeilen;
}

/*
 * Beträge der drei Karten „Wie viele“ einer Farbe (05 6.1, 04 A3.4) und der Preistreppe (6.7):
 *   eine: { ab }  kleinster bestellbarer Einzelpreis („ab 54,99 €“)
 *   zwei: { ab, ersparnisAb }  kleinster Preis der kaufbaren Pfannen-Duos („ab 87,98 €“) und die kleinste Ersparnis
 *         unter ihnen (E-171); Duo 20 % (01.10.2026): nur Paare, die die Wache zweierPaar freigibt
 *   set:  { preis, verfuegbar, vergleich }  vergleich = setErsparnis oder null: die drei vollen Einzelpreise
 *         gegen das Set (E-169, das zeigt die Set-Karte)
 * Ist nichts bestellbar, ist „ab“ null.
 */
export function buyboxKarten(daten, farbe) {
  const einzelFarbe = einzelDerFarbe(daten, farbe);
  const lieferbar = GROESSEN.map((g) => eintragGroesse(einzelFarbe, g)).filter((e) => verfuegbarVon(e));
  const eineAb = lieferbar.length ? Math.min(...lieferbar.map(preisVon)) : null;
  const zweier = zweierTabelle(daten, farbe).filter((z) => z.kaufbar);
  const zweiAb = zweier.length ? Math.min(...zweier.map((z) => z.summe)) : null;
  const s = setDerFarbe(daten, farbe);
  return {
    eine: { ab: eineAb },
    zwei: { ab: zweiAb, ersparnisAb: zweier.length ? Math.min(...zweier.map((z) => z.ersparnis)) : null },
    set: {
      preis: preisVon(s),
      verfuegbar: verfuegbarVon(s),
      vergleich: setErsparnis(GROESSEN.map((g) => eintragGroesse(einzelFarbe, g)), s)
    }
  };
}

/* ---------- Bestand: was wählbar ist (Rückmeldung 25.09.2026) ---------- */

/*
 * Was in einer Farbe wählbar ist. Ausverkauft heißt: nicht wählbar und nicht kaufbar, angezeigt als
 * „nicht verfügbar“, ohne Zahlen und ohne Knappheit. Grundlage ist nur „verfuegbar“ der Varianten
 * (Shopify variant.available), nie eine Stückzahl.
 * Rückgabe { farbe, eine, zwei, set, groessen: { 20, 24, 28 }, liste }:
 *   groessen[g]  Einzelpfanne g in der Farbe bestellbar
 *   eine         mindestens eine Größe bestellbar
 *   zwei         Duo 20 % (01.10.2026): mindestens ein Paar als Pfannen-Duo kaufbar (zweierPaar)
 *   set          Set der Farbe bestellbar
 *   farbe        irgendetwas in der Farbe bestellbar
 *   liste        bestellbare Größen, aufsteigend
 */
export function wahlMoeglich(daten, farbe) {
  const einzelFarbe = einzelDerFarbe(daten, farbe);
  const groessen = {};
  GROESSEN.forEach((g) => {
    groessen[g] = verfuegbarVon(eintragGroesse(einzelFarbe, g));
  });
  const liste = GROESSEN.filter((g) => groessen[g]);
  const eine = liste.length > 0;
  const zwei = zweierTabelle(daten, farbe).some((z) => z.kaufbar);
  const set = verfuegbarVon(setDerFarbe(daten, farbe));
  return { farbe: eine || zwei || set, eine, zwei, set, groessen, liste };
}

/*
 * Nächste bestellbare Größe zu g in der Farbe: g selbst, sonst die mit dem kleinsten Abstand,
 * bei gleichem Abstand die größere (20 -> 24, 24 -> 28, 28 -> 24). null, wenn keine Größe geht.
 */
export function naechsteGroesse(daten, farbe, g) {
  const m = wahlMoeglich(daten, farbe);
  const ziel = Number(g);
  if (m.groessen[ziel]) return ziel;
  if (!m.liste.length) return null;
  return m.liste.slice().sort((a, b) => Math.abs(a - ziel) - Math.abs(b - ziel) || b - a)[0];
}

/*
 * Auswahl an den Bestand anpassen, ohne die Wahl der Kundin umzuwerfen, wo es nicht nötig ist:
 *   1. Geht in der Farbe gar nichts, die andere Farbe (wenn dort etwas geht).
 *   2. Geht der Modus in der Farbe nicht (Duo 20 % (01.10.2026)): Set -> Zwei Pfannen, sonst Eine Pfanne;
 *      Zwei Pfannen -> Set, sonst Eine Pfanne; Eine Pfanne -> Set, sonst Zwei Pfannen.
 *   3. Nicht bestellbare Größen -> nächste bestellbare Größe (naechsteGroesse). Ein Paar, dessen Pfannen-Duo gesperrt
 *      ist, bleibt stehen (Knopf gesperrt, Text zweier_gesperrt), es springt nicht.
 *   4. Zusatz aus, wenn Bambus nicht bestellbar ist (die Bambus-Zeile steht dann ohnehin nicht im Formular).
 * Geht nirgends etwas, bleibt die Wahl, wie sie ist (Knopf gesperrt, Text „nicht verfügbar“).
 * Rückgabe: neue Wahl (die alte wird nicht verändert) mit geaendert: true|false.
 */
export function wahlKorrigieren(wahl, daten) {
  const w = Object.assign({}, wahl);
  const farbeNr = (f) => (Number(f) === 2 ? 2 : 1);
  w.farbe = farbeNr(w.farbe);
  const vorher = JSON.stringify([w.modus, w.farbe, w.groesse1, w.groesse2, !!w.zusatz]);
  let m = wahlMoeglich(daten, w.farbe);
  if (!m.farbe) {
    const andere = w.farbe === 1 ? 2 : 1;
    const mo = wahlMoeglich(daten, andere);
    if (!mo.farbe) return Object.assign(w, { geaendert: false });
    w.farbe = andere;
    m = mo;
  }
  if (!m[w.modus]) {
    if (w.modus === 'set') w.modus = m.zwei ? 'zwei' : m.eine ? 'eine' : w.modus;
    else if (w.modus === 'zwei') w.modus = m.set ? 'set' : m.eine ? 'eine' : w.modus;
    else w.modus = m.set ? 'set' : m.zwei ? 'zwei' : w.modus;
  }
  if (w.modus === 'eine' || w.modus === 'zwei') {
    const g1 = naechsteGroesse(daten, w.farbe, w.groesse1);
    if (g1 !== null) w.groesse1 = g1;
    if (w.modus === 'zwei') {
      const g2 = naechsteGroesse(daten, w.farbe, w.groesse2);
      if (g2 !== null) w.groesse2 = g2;
    }
  }
  if (w.zusatz && !verfuegbarVon(daten && daten.bambus)) w.zusatz = false;
  const nachher = JSON.stringify([w.modus, w.farbe, w.groesse1, w.groesse2, !!w.zusatz]);
  w.geaendert = vorher !== nachher;
  return w;
}

/*
 * Alle drei Größen: einzeln gegen das Set (02 Abschnitt 3). Duo 20 % (01.10.2026): Drei Einzelpfannen kosten ihre vollen
 * Preise (einzeln), der Weg über das Pfannen-Duo 24 + 28 cm plus die 20 cm einzeln kostet duoWeg ({duo_weg} der FAQ).
 * Belegt „Der günstigste Weg zu allen drei Größen“ (setGuenstiger: das Set kostet weniger als einzeln und als jeder der
 * drei Wege Duo plus dritte Größe) und zeigt, warum „Bester Preis pro Pfanne“ nicht haltbar ist
 * (setBesterPreisJePfanne false, weil ein Pfannen-Duo je Pfanne weniger kosten kann). Reine Rechnung mit der Formel,
 * ohne Wache; ohne gültigen Prozentwert sind duoWeg und jePfanneZweierMin null.
 */
export function alleDreiVergleich(preiseFarbe, setCent, prozent) {
  const set = preisVon(setCent);
  const preise = GROESSEN.map((g) => preisVon(eintragGroesse(preiseFarbe, g)));
  if (set === null || preise.some((p) => p === null)) return null;
  const [p20, p24, p28] = preise;
  const einzeln = p20 + p24 + p28;
  const duoPlus = (a, b, dritte) => {
    const s = summeZweier(a, b, prozent);
    return s === null ? null : s + dritte;
  };
  const duoWeg = duoPlus(p24, p28, p20);
  const wege = [duoWeg, duoPlus(p20, p28, p24), duoPlus(p20, p24, p28)].filter((w) => w !== null);
  const jePfanneZweier = [];
  for (let i = 0; i < preise.length; i += 1) {
    for (let k = i; k < preise.length; k += 1) {
      const s = summeZweier(preise[i], preise[k], prozent);
      if (s !== null) jePfanneZweier.push(halbAuf(s / 2));
    }
  }
  const jePfanneZweierMin = jePfanneZweier.length ? Math.min(...jePfanneZweier) : null;
  const jePfanneSet = halbAuf(set / 3);
  return {
    einzeln,
    duoWeg,
    set,
    setGuenstiger: set < Math.min(einzeln, ...wege),
    jePfanneSet,
    jePfanneZweierMin,
    setBesterPreisJePfanne: jePfanneZweierMin === null || jePfanneSet <= jePfanneZweierMin
  };
}

/* ---------- Warenkorb: Positionen aus /cart.js ---------- */

/*
 * Zuordnung Varianten-ID -> { art, groesse, farbe } aus der Datenform oben (ids aus den Einträgen). Duo 20 % (01.10.2026):
 * die Pfannen-Duos aus daten.duoVarianten als { art: 'duo', groesse: null, farbe, paar: [a, b] }, kleine Größe zuerst.
 */
export function variantenAusDaten(daten) {
  const karte = new Map();
  if (!daten) return karte;
  Object.keys(daten.einzel || {}).forEach((farbe) => {
    const jeGroesse = daten.einzel[farbe] || {};
    Object.keys(jeGroesse).forEach((g) => {
      const id = idVon(jeGroesse[g]);
      if (id != null) karte.set(String(id), { art: 'einzel', groesse: Number(g), farbe: String(farbe) });
    });
  });
  Object.keys(daten.set || {}).forEach((farbe) => {
    const id = idVon(daten.set[farbe]);
    if (id != null) karte.set(String(id), { art: 'set', groesse: null, farbe: String(farbe) });
  });
  const bid = idVon(daten.bambus);
  if (bid != null) karte.set(String(bid), { art: 'bambus', groesse: null, farbe: null });
  Object.keys(daten.duoVarianten || {}).forEach((schluessel) => {
    const m = /^(\d+)-(\d+)\|([12])$/.exec(schluessel);
    const id = idVon(daten.duoVarianten[schluessel]);
    if (!m || id == null) return;
    const a = Number(m[1]);
    const b = Number(m[2]);
    karte.set(String(id), { art: 'duo', groesse: null, farbe: m[3], paar: [Math.min(a, b), Math.max(a, b)] });
  });
  return karte;
}

/*
 * Positionen aus der Antwort von /cart.js (oder add.js/change.js), in derselben Reihenfolge.
 * varianten: Map oder Objekt Varianten-ID -> { art: 'einzel'|'duo'|'set'|'bambus', groesse, farbe, paar }.
 * Unbekannte Varianten bekommen art 'fremd' (zählen in Anzahl und Summe, nicht für Karten). Duo 20 % (01.10.2026): Ein
 * Pfannen-Duo ist eine Zeile (Messung M1: Bundle mit has_components, die Bestandteile stehen nicht in items) und trägt
 * paar [a, b]; ohne duoVarianten in den Daten bleibt es 'fremd'.
 * Beträge nur aus Shopify: einzelpreis = original_price, linienPreis = final_line_price,
 * nachlass = line_level_total_discount. codeNachlass (E-169) = davon aus Rabattcodes, nur gesetzt, wenn größer 0.
 */
export function positionenAusCart(cart, varianten) {
  const items = cart && Array.isArray(cart.items) ? cart.items : [];
  const nachschlagen = (id) => {
    if (!varianten || id == null) return null;
    if (varianten instanceof Map) return varianten.get(String(id)) || varianten.get(Number(id)) || null;
    return varianten[String(id)] || null;
  };
  return items.map((it) => {
    const id = it.variant_id != null ? it.variant_id : it.id;
    const info = nachschlagen(id);
    const menge = zahl(it.quantity) || 0;
    const einzelpreis = zahl(it.original_price) !== null ? zahl(it.original_price) : zahl(it.price);
    let nachlass = zahl(it.line_level_total_discount);
    if (nachlass === null) {
      const alloc = Array.isArray(it.line_level_discount_allocations) ? it.line_level_discount_allocations : [];
      nachlass = summe(alloc, (a) => zahl(a.amount) || 0);
    }
    let linienPreis = zahl(it.final_line_price);
    if (linienPreis === null) linienPreis = zahl(it.line_price);
    if (linienPreis === null && einzelpreis !== null) linienPreis = einzelpreis * menge - nachlass;
    const zuteilungen = Array.isArray(it.line_level_discount_allocations) ? it.line_level_discount_allocations : [];
    const codeNachlass = summe(zuteilungen, (a) => (a && a.discount_application && a.discount_application.type === 'discount_code' ? zahl(a.amount) || 0 : 0));
    const position = {
      key: it.key != null ? it.key : null,
      id,
      art: info ? info.art : 'fremd',
      groesse: info && info.groesse != null ? Number(info.groesse) : null,
      farbe: info && info.farbe != null ? String(info.farbe) : null,
      menge,
      einzelpreis,
      linienPreis,
      nachlass
    };
    if (info && Array.isArray(info.paar)) position.paar = info.paar.slice(0, 2).map(Number);
    if (codeNachlass > 0) position.codeNachlass = codeNachlass;
    return position;
  });
}

function linienPreisVon(p) {
  if (zahl(p.linienPreis) !== null) return p.linienPreis;
  if (zahl(p.einzelpreis) !== null) return p.einzelpreis * p.menge - (zahl(p.nachlass) || 0);
  return null;
}

/* ---------- Warenkorb: Karten „Passt dazu“ und Versandzeile (02 Abschnitt 7) ---------- */

// Summe nach der Aktion einer Karte; null, wenn die Karte kein weiteres Produkt bringt (Tausch)
function summeNachKarte(karte) {
  if (!karte || !karte.bringtProdukt) return null;
  return zahl(karte.summeDanach);
}

/*
 * Versandzeile (02 Abschnitt 7, 04 A5.3):
 *   1. Summe >= Schwelle: frei
 *   2. sonst, wenn die ERSTE Karte die Summe nachgerechnet über die Schwelle hebt: noch_ein_produkt
 *   3. sonst: rueckfall
 * Alle Beträge in Cent. Rückgabe { frei, zeile, text, anteil, schwelle } oder null ohne Werte.
 */
export function versandZeile({ gesamt, schwelle, ersteKarte = null, texte = VERSAND_TEXTE } = {}) {
  const s = zahl(gesamt);
  const w = zahl(schwelle);
  if (s === null || w === null) return null;
  const t = Object.assign({}, VERSAND_TEXTE, texte || {});
  const anteil = versandAnteil(s, w);
  if (versandFrei(s, w)) return { frei: true, zeile: 'frei', text: t.frei, anteil, schwelle: w };
  const danach = summeNachKarte(ersteKarte);
  if (danach !== null && danach >= w) return { frei: false, zeile: 'noch_ein_produkt', text: t.noch_ein_produkt, anteil, schwelle: w };
  return { frei: false, zeile: 'rueckfall', text: t.rueckfall, anteil, schwelle: w };
}

/*
 * Karten und Versandzeile für Drawer und Warenkorbseite (02 Abschnitt 7).
 *
 * Duo 20 % (01.10.2026): Den Nachlass für zwei Pfannen gibt es nur als Pfannen-Duo. Die Karten bieten deshalb den Tausch
 * an: eine Einzelpfanne gegen ein Pfannen-Duo („Zweite Pfanne dazu“), zwei Einzelpfannen einer Farbe gegen ihr
 * Pfannen-Duo („Als Pfannen-Duo nehmen“) und ein Pfannen-Duo aus zwei Größen gegen das Set. Duo-Karten nur, wenn die
 * Wache zweierPaar das Paar freigibt (Variante da, lieferbar, Preis centgenau summeZweier). Was jetzt im Warenkorb liegt,
 * zählt mit den Zeilenpreisen aus /cart.js (final_line_price, also nach Rabattcodes, Gegenprüfung M7), die neue Ware
 * mit ihrem Listenpreis; eine Ersparnis wird so nie zu hoch genannt.
 *
 * eingabe:
 *   positionen     aus positionenAusCart (Reihenfolge wie /cart.js)
 *   gesamt         cart.total_price in Cent (fehlt er, gilt die Summe der Zeilen)
 *   daten          Datenform oben (prozent, schwelle, einzel, set, bambus, duoVarianten)
 *   zuletztFarbe   Farbe der zuletzt hinzugefügten Pfanne, wenn rs-cart.js sie kennt
 *   neuesteZuerst  true (Standard): /cart.js führt die neueste Position zuerst; nur ohne zuletztFarbe
 *   tauschAn       settings.rs_tausch_an, Tausch gegen das Set (Standard true)
 *   bambusAn       Bambus-Karte erlaubt (Produkt gesetzt, PH-80 erfüllt; Standard true)
 *   gesperrt       Varianten-IDs, für die bis zum Neuladen keine Karte erscheint (Tausch mit 422 abgelehnt,
 *                  Gegenprüfung H2)
 *   texte          eigene Texte für die Versandzeile (Schlüssel wie VERSAND_TEXTE)
 *
 * Rückgabe:
 *   leer            true, wenn nach der Bambus-Regel nichts im Warenkorb bleibt
 *   anzahl          Artikel (Summe der Mengen) nach der Bambus-Regel; ein Pfannen-Duo zählt wie bei Shopify einmal
 *   farbe           Farbe der Karten (zuletzt hinzugefügte Einzelpfanne, Pfannen-Duo oder Set)
 *   bambusEntfernen [{ key, id }]: Bambus ohne Pfanne, per change.js auf 0 setzen (04 A5.7)
 *   stueckEinzel    Anzahl Einzelpfannen
 *   versand         versandZeile(...) oder null bei leerem Warenkorb
 *   karten          höchstens zwei, in dieser Reihenfolge: zweite oder duo und set oder tausch, danach bambus
 *     { art: 'zweite', farbe, groesse, optionen: [{ groesse, id, beideZusammen, summeDanach, entfernen }], summeDanach,
 *       bringtProdukt: true }
 *       genau eine Einzelpfanne, kein Set, kein Pfannen-Duo: je Größe das Pfannen-Duo mit ihr (id und beideZusammen
 *       der Duo-Variante); entfernen [{ key, id, weniger: 1 }] nimmt die Einzelpfanne dabei heraus
 *     { art: 'duo', farbe, paar: [a, b], duoId, duoPreis, jetzt, ersparnis, summeDanach, entfernen, bringtProdukt: false }
 *       genau zwei Einzelpfannen einer Farbe (auch eine Zeile mit Menge 2), ein Set oder Pfannen-Duo daneben stört nicht:
 *       jetzt = ihre Zeilenpreise, ersparnis = jetzt minus Duo-Preis (nur über 0); entfernen [{ key, id, weniger }] je
 *       Variante (Shopify teilt eine Variante unter Umständen in zwei Zeilen)
 *     { art: 'set', farbe, paar: [a, b], fehlt, ausDuo, setPreis, setId, jetzt, aufpreis, dritteEinzeln, einzelnDrei,
 *       mehrEinzeln, summeDanach, entfernen: [{ key, id, weniger }], bringtProdukt: true }
 *       Grundlage zwei Einzelpfannen verschiedener Größen einer Farbe ohne Pfannen-Duo, oder ein Pfannen-Duo aus zwei
 *       Größen mit Menge 1 ohne Einzelpfannen (ausDuo). einzelnDrei: die drei vollen Einzelpreise (R64); dritteEinzeln
 *       und mehrEinzeln: Preis der fehlenden Größe. Nur mit allen drei Größen bestellbar und 0 < aufpreis < mehrEinzeln.
 *     { art: 'tausch', variante: 'drei'|'vier', farbe, setPreis, setId, jetzt, nachher, ersparnis, summeDanach,
 *       entfernen: [{ key, id, groesse, weniger: 1 }], bringtProdukt: false }
 *     { art: 'bambus', preis, id, summeDanach, bringtProdukt }
 */
export function warenkorbKarten(eingabe) {
  const e = eingabe || {};
  const daten = e.daten || {};
  const tauschAn = e.tauschAn !== false;
  const bambusAn = e.bambusAn !== false;
  const neuesteZuerst = e.neuesteZuerst !== false;
  const prozent = zahl(daten.prozent);
  const gesperrt = new Set(Array.from(e.gesperrt || [], (id) => String(id)));
  const frei = (id) => id != null && !gesperrt.has(String(id));

  let pos = (Array.isArray(e.positionen) ? e.positionen : []).filter((p) => p && zahl(p.menge) > 0);

  // Bambus-Regel (02 7.4, 05 8.3): bleibt nur Bambus übrig, wird er entfernt.
  // Nur wenn sonst gar nichts im Warenkorb liegt, damit eine unbekannte Variante nie dazu führt.
  const bambusPos = pos.filter((p) => p.art === 'bambus');
  const ohneBambus = pos.filter((p) => p.art !== 'bambus');
  const bambusEntfernen = bambusPos.length && !ohneBambus.length ? bambusPos.map((p) => ({ key: p.key, id: p.id })) : [];
  if (bambusEntfernen.length) pos = ohneBambus;

  const leerErgebnis = { leer: true, anzahl: 0, farbe: null, bambusEntfernen, karten: [], versand: null, stueckEinzel: 0 };
  if (!pos.length) return leerErgebnis;

  const anzahl = summe(pos, (p) => p.menge);
  let gesamt = zahl(e.gesamt);
  if (gesamt === null) gesamt = summe(pos, (p) => linienPreisVon(p) || 0);

  const einzel = pos.filter((p) => p.art === 'einzel');
  const duos = pos.filter((p) => p.art === 'duo');
  const sets = pos.filter((p) => p.art === 'set');
  const hatBambus = pos.some((p) => p.art === 'bambus');
  const stueckEinzel = summe(einzel, (p) => p.menge);

  // Farbe der Karten: zuletzt hinzugefügte Pfanne, auch ein Pfannen-Duo (02 Abschnitt 7 und 9)
  const pfannen = pos.filter((p) => (p.art === 'einzel' || p.art === 'duo' || p.art === 'set') && p.farbe != null);
  const neueste = neuesteZuerst ? pfannen : pfannen.slice().reverse();
  const gewuenscht = e.zuletztFarbe != null ? String(e.zuletztFarbe) : null;
  const farbe = gewuenscht && pfannen.some((p) => p.farbe === gewuenscht) ? gewuenscht : neueste.length ? neueste[0].farbe : null;

  const karten = [];
  const einzelFarbe = einzelDerFarbe(daten, farbe);
  const setEintrag = setDerFarbe(daten, farbe);
  const setPreis = preisVon(setEintrag);
  // Tausch gegen das Set (Einstellung rs_tausch_an), nie neben einem Set im Warenkorb
  const setMoeglich = tauschAn && !sets.length && setPreis !== null && verfuegbarVon(setEintrag) && frei(idVon(setEintrag));
  // Go-live-Liste E1: ohne Nachlass für zwei Pfannen (0) keine Duo-Karten
  const mitDuo = prozent !== null && prozent > 0;
  const einfarbig = einzel.length > 0 && einzel.every((p) => p.farbe != null && p.farbe === einzel[0].farbe);
  const alleBekannt = einzel.every((p) => GROESSEN.includes(p.groesse));
  // Zeilenpreise einer Grundlage, null, wenn einer fehlt
  const zeilenSumme = (liste) => {
    const preise = liste.map(linienPreisVon);
    return preise.every((x) => x !== null) ? summe(preise, (x) => x) : null;
  };
  // je Variante zusammengefasst: Shopify kann eine Variante in zwei Zeilen teilen (Regel „Kaufe 1, erhalte 1“)
  const jeVariante = (liste) => {
    const m = new Map();
    liste.forEach((p) => {
      const k = String(p.id);
      const x = m.get(k) || { key: p.key, id: p.id, weniger: 0 };
      x.weniger += p.menge;
      m.set(k, x);
    });
    return Array.from(m.values());
  };

  if (!sets.length && !duos.length && stueckEinzel === 1 && mitDuo) {
    // 7.1: eine Einzelpfanne, Karte „Zweite Pfanne dazu“. Duo 20 % (01.10.2026): Sie tauscht die Pfanne gegen das
    // Pfannen-Duo mit der gewählten Größe, je Größe nur mit freigegebenem Paar in ihrer Farbe.
    const p = einzel[0];
    const optionen = [];
    GROESSEN.forEach((g) => {
      const pz = zweierPaar(daten, p.farbe, p.groesse, g);
      if (!pz || !pz.kaufbar || !frei(pz.id)) return;
      optionen.push({
        groesse: g,
        id: pz.id,
        beideZusammen: pz.summe,
        summeDanach: gesamt - (linienPreisVon(p) || 0) + pz.summe,
        entfernen: [{ key: p.key, id: p.id, weniger: 1 }]
      });
    });
    if (optionen.length) {
      karten.push({
        art: 'zweite',
        farbe: p.farbe,
        groesse: p.groesse,
        optionen,
        summeDanach: Math.min(...optionen.map((o) => o.summeDanach)),
        bringtProdukt: true
      });
    }
  }

  if (stueckEinzel === 2 && einfarbig && alleBekannt && mitDuo) {
    // Duo 20 % (01.10.2026): zwei Einzelpfannen einer Farbe, Karte „Als Pfannen-Duo nehmen“
    const [a, b] = einzel.flatMap((p) => Array(p.menge).fill(p.groesse)).sort((x, y) => x - y);
    const pz = zweierPaar(daten, einzel[0].farbe, a, b);
    const jetzt = zeilenSumme(einzel);
    if (pz && pz.kaufbar && frei(pz.id) && jetzt !== null && pz.summe < jetzt) {
      karten.push({
        art: 'duo',
        farbe: einzel[0].farbe,
        paar: [a, b],
        duoId: pz.id,
        duoPreis: pz.summe,
        jetzt,
        ersparnis: jetzt - pz.summe,
        summeDanach: gesamt - jetzt + pz.summe,
        entfernen: jeVariante(einzel),
        bringtProdukt: false
      });
    }
  }

  if (setMoeglich && stueckEinzel <= 2) {
    // 7.2: „Mach ein Set draus“ mit echtem Aufpreis. Grundlage zwei Einzelpfannen verschiedener Größen einer Farbe ohne
    // Pfannen-Duo, oder (Duo 20 % (01.10.2026)) ein Pfannen-Duo aus zwei Größen mit Menge 1 ohne Einzelpfannen.
    let grundlage = null;
    if (!duos.length && stueckEinzel === 2 && einfarbig && alleBekannt && einzel[0].farbe === farbe) {
      const paar = Array.from(new Set(einzel.map((p) => p.groesse))).sort((x, y) => x - y);
      if (paar.length === 2) grundlage = { paar, positionen: einzel, ausDuo: false };
    } else if (!einzel.length && duos.length === 1 && duos[0].menge === 1 && duos[0].farbe === farbe && Array.isArray(duos[0].paar)) {
      const paar = duos[0].paar.map(Number).sort((x, y) => x - y);
      if (paar.length === 2 && paar[0] !== paar[1] && paar.every((g) => GROESSEN.includes(g))) grundlage = { paar, positionen: duos, ausDuo: true };
    }
    if (grundlage) {
      const fehlt = GROESSEN.find((g) => !grundlage.paar.includes(g));
      const drei = GROESSEN.map((g) => eintragGroesse(einzelFarbe, g));
      // R64: einzeln kosten die drei ihre vollen Preise, verglichen nur, wenn alle drei bestellbar sind (07 3.3)
      const einzelnDrei = drei.every((x) => verfuegbarVon(x)) ? summe(drei, (x) => preisVon(x)) : null;
      const dritte = eintragGroesse(einzelFarbe, fehlt);
      const dritteEinzeln = verfuegbarVon(dritte) ? preisVon(dritte) : null;
      const jetzt = zeilenSumme(grundlage.positionen);
      const aufpreis = jetzt !== null ? setPreis - jetzt : null;
      // nur, wenn das Set wirklich der günstigere Weg zur dritten Größe ist (Auftrag 5.3: nachrechnen)
      if (fehlt && einzelnDrei !== null && dritteEinzeln !== null && aufpreis !== null && aufpreis > 0 && aufpreis < dritteEinzeln) {
        karten.push({
          art: 'set',
          farbe,
          paar: grundlage.paar,
          fehlt,
          ausDuo: grundlage.ausDuo,
          setPreis,
          setId: idVon(setEintrag),
          jetzt,
          aufpreis,
          dritteEinzeln,
          einzelnDrei,
          mehrEinzeln: dritteEinzeln,
          summeDanach: gesamt + aufpreis,
          entfernen: jeVariante(grundlage.positionen),
          bringtProdukt: true
        });
      }
    }
  }

  if (setMoeglich && stueckEinzel >= 3) {
    // 7.3: alle drei Größen in der Kartenfarbe, Tausch nur, wenn nachgerechnet günstiger
    const derFarbe = einzel.filter((p) => p.farbe === farbe);
    const gewaehlt = GROESSEN.map((g) => derFarbe.find((p) => p.groesse === g && p.menge >= 1));
    if (gewaehlt.every(Boolean)) {
      const jetztEinzel = summe(einzel, (p) => linienPreisVon(p) || 0);
      const rest = einzel
        .map((p) => ({ p, menge: gewaehlt.includes(p) ? p.menge - 1 : p.menge }))
        .filter((x) => x.menge > 0);
      const restStueck = summe(rest, (x) => x.menge);
      const variante = restStueck === 0 ? 'drei' : restStueck === 1 ? 'vier' : null;
      const restPreise = rest.map((x) => ({ cent: zahl(x.p.einzelpreis), menge: x.menge, farbe: x.p.farbe }));
      // die übrige Pfanne bleibt zu ihrem vollen Preis im Warenkorb (keine Paarbildung, Duo 20 % (01.10.2026))
      const restSumme = variante ? summeWarenkorb({ einzel: restPreise }) : null;
      if (variante && restSumme) {
        const nachher = setPreis + restSumme.gesamt;
        if (nachher < jetztEinzel) {
          karten.push({
            art: 'tausch',
            variante,
            farbe,
            setPreis,
            setId: idVon(setEintrag),
            jetzt: jetztEinzel,
            nachher,
            ersparnis: jetztEinzel - nachher,
            summeDanach: gesamt - (jetztEinzel - nachher),
            entfernen: gewaehlt.map((p) => ({ key: p.key, id: p.id, groesse: p.groesse, weniger: 1 })),
            bringtProdukt: false
          });
        }
      }
    }
  }

  // 7.1 bis 7.4: Bambus immer zuletzt, nie doppelt, bei einem Set die einzige Karte; ein Pfannen-Duo zählt als Pfanne
  const bambus = daten.bambus;
  if (!hatBambus && bambusAn && verfuegbarVon(bambus)) {
    const preis = preisVon(bambus);
    karten.push({ art: 'bambus', preis, id: idVon(bambus), summeDanach: gesamt + preis, bringtProdukt: true });
  }

  const sichtbar = karten.slice(0, 2);
  return {
    leer: false,
    anzahl,
    farbe,
    bambusEntfernen,
    karten: sichtbar,
    versand: versandZeile({ gesamt, schwelle: daten.schwelle, ersteKarte: sichtbar[0] || null, texte: e.texte }),
    stueckEinzel
  };
}

/* ---------- Größe aus einem Optionswert (05 8.2) ---------- */

// „Alltagsheld, 24 cm“ -> 24; ohne Zahl vor „cm“ null (dann gilt die Reihenfolge der Varianten)
export function groesseAusText(text) {
  if (text == null) return null;
  const m = String(text).match(/(\d+)(?:[.,]\d+)?\s*cm\b/i);
  return m ? Number(m[1]) : null;
}

/* ---------- Ausgabe ---------- */

const RSPreise = Object.freeze({
  GROESSEN,
  VERSAND_TEXTE,
  preisVon,
  verfuegbarVon,
  formatEuro,
  formatEuroSprache,
  euroZuCent,
  summeZweier,
  ersparnisZweier,
  duoSchluessel,
  zweierPaar,
  summeWarenkorb,
  vergleich,
  setErsparnis,
  versandFrei,
  versandAnteil,
  preisDerWahl,
  vergleichDerWahl,
  zweierTabelle,
  buyboxKarten,
  wahlMoeglich,
  naechsteGroesse,
  wahlKorrigieren,
  alleDreiVergleich,
  variantenAusDaten,
  positionenAusCart,
  versandZeile,
  warenkorbKarten,
  groesseAusText
});

export default RSPreise;

if (typeof window !== 'undefined') {
  window.RSPreise = RSPreise;
}
